import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { Modal, Button } from "react-bootstrap";
import axiosInstance from "services/AxiosInstance";
import {
  notifyError,
  notifySuccess,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import Tables from "jsx/components/CitoPlus/components/tables";
import Select from "react-select";
import swal from "sweetalert";
import { ButtonGroup } from "react-bootstrap";
import {
  CitoPlusLinkIconPrimary,
  CitoPlusLinkIconDanger,
  CitoPlusLinkIconSuccess,
  CitoPlusLinkIconWarning,
} from "jsx/components/CitoPlus/components/CitoPlusLink";
import { useMantenimientoActions } from "./hooks/useMantenimientoActions";
import MantenimientoDetailsModal from "./components/MantenimientoDetailsModal";
import moment from "moment";

const MANTENIMIENTOS_URL = "/api/v3/zonas-sociales/activos/mantenimientos/";
const ACTIVOS_URL = "/api/v3/zonas-sociales/activos/";

const mantenimientoSchema = Yup.object().shape({
  activo_id: Yup.number().required("El activo es obligatorio."),
  titulo: Yup.string().required("El título es obligatorio."),
  descripcion: Yup.string(),
  fecha_mantenimiento: Yup.date().required("La fecha de mantenimiento es obligatoria."),
  fecha_proximo_mantenimiento: Yup.date().nullable(),
  costo_mantenimiento: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? null : value,
    ),
});

const ActivosMantenimientos = () => {
  const [mantenimientos, setMantenimientos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const {
    iniciarMantenimiento,
    completarMantenimiento,
    cancelarMantenimiento,
  } = useMantenimientoActions();

  const [activoFilter, setActivoFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTermDebounced, setSearchTermDebounced] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const [activos, setActivos] = useState([]);
  const [isLoadingActivos, setIsLoadingActivos] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);
  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedMantenimiento, setSelectedMantenimiento] = useState(null);
  const [selectedMantenimientoData, setSelectedMantenimientoData] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  // Ref para mantener valores actuales sin causar re-renders
  const filtersRef = useRef({
    activoFilter: null,
    searchTermDebounced: "",
    fechaDesde: "",
    fechaHasta: "",
    sortField: "",
    order: "asc",
  });

  // Actualizar refs cuando cambien los valores
  useEffect(() => {
    filtersRef.current = {
      activoFilter,
      searchTermDebounced,
      fechaDesde,
      fechaHasta,
      sortField,
      order,
    };
  }, [activoFilter, searchTermDebounced, fechaDesde, fechaHasta, sortField, order]);

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTermDebounced(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Consolidar efectos de filtros en uno solo
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMantenimientos(1);
    }, 0);
    return () => clearTimeout(timer);
  }, [activoFilter, searchTermDebounced, fechaDesde, fechaHasta]);

  const fetchActivos = useCallback(async () => {
    setIsLoadingActivos(true);
    try {
      const response = await axiosInstance.get(ACTIVOS_URL, {
        params: { page_size: 100 },
      });
      const results = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setActivos(results);
    } catch (error) {
      notifyError("No fue posible obtener el listado de activos.");
      setActivos([]);
    } finally {
      setIsLoadingActivos(false);
    }
  }, []);

  const fetchMantenimientos = useCallback(
    async (pageOrOptions = 1) => {
      const filters = filtersRef.current;
      
      const page =
        typeof pageOrOptions === "number"
          ? pageOrOptions
          : pageOrOptions?.page || currentPage || 1;

      const urlOverride =
        typeof pageOrOptions === "object" && pageOrOptions?.url
          ? pageOrOptions.url
          : null;

      setIsLoading(true);
      try {
        const params = {};

        if (!urlOverride) {
          params.page = page;
          if (filters.activoFilter?.value) {
            params.activo_id = filters.activoFilter.value;
          }
          if (filters.searchTermDebounced.trim()) {
            params.search = filters.searchTermDebounced.trim();
          }
          if (filters.fechaDesde) {
            params.fecha_desde = filters.fechaDesde;
          }
          if (filters.fechaHasta) {
            params.fecha_hasta = filters.fechaHasta;
          }
          if (filters.sortField) {
            params.ordering = `${filters.order === "desc" ? "-" : ""}${filters.sortField}`;
          }
        }

        const response = await axiosInstance.get(
          urlOverride || MANTENIMIENTOS_URL,
          urlOverride ? {} : { params },
        );

        const payload = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        setMantenimientos(payload);

        const count =
          typeof response.data?.count === "number"
            ? response.data.count
            : Array.isArray(response.data)
              ? response.data.length
              : payload.length;

        const fetchedTotalPages =
          response.data?.total_pages ??
          (count > 0 ? Math.ceil(count / (response.data?.page_size ?? 25)) : 1);

        setTotalRecords(count);
        setTotalPages(Math.max(fetchedTotalPages, 1));
        const resolvedPage =
          response.data?.current_page ?? response.data?.page ?? page;
        setCurrentPage(resolvedPage);
        setNextUrl(response.data?.next ?? null);
        setPreviousUrl(response.data?.previous ?? null);
      } catch (error) {
        notifyError(
          "No fue posible obtener el listado de mantenimientos. Intenta de nuevo más tarde.",
        );
        setMantenimientos([]);
        setTotalRecords(0);
        setTotalPages(1);
        setCurrentPage(1);
        setNextUrl(null);
        setPreviousUrl(null);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage],
  );

  useEffect(() => {
    fetchActivos();
  }, [fetchActivos]);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleActivoFilterChange = useCallback((option) => {
    setActivoFilter(option);
  }, []);

  const handleFechaDesdeChange = useCallback((event) => {
    setFechaDesde(event.target.value);
  }, []);

  const handleFechaHastaChange = useCallback((event) => {
    setFechaHasta(event.target.value);
  }, []);

  const handleResetFilters = useCallback(() => {
    setActivoFilter(null);
    setSearchTerm("");
    setSearchTermDebounced("");
    setFechaDesde("");
    setFechaHasta("");
    setSortField("");
    setOrder("asc");
    fetchMantenimientos(1);
  }, [fetchMantenimientos]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const params = {};
      if (activoFilter?.value) params.activo = activoFilter.value;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;

      const response = await axiosInstance.get(`${MANTENIMIENTOS_URL}exportar/`, {
        params,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `mantenimientos-${moment().format("YYYY-MM-DD")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notifyError("No fue posible exportar los mantenimientos.");
    } finally {
      setIsExporting(false);
    }
  }, [activoFilter, searchTerm, fechaDesde, fechaHasta]);

  const handleConsult = useCallback(() => {
    fetchMantenimientos(1);
  }, [fetchMantenimientos]);

  const handleSortingChange = useCallback(
    (column) => {
      if (!column?.id) {
        return;
      }

      const nextOrder =
        sortField === column.id ? (order === "asc" ? "desc" : "asc") : "asc";

      setSortField(column.id);
      setOrder(nextOrder);

      fetchMantenimientos(1);
    },
    [fetchMantenimientos, order, sortField],
  );

  const handleEdit = useCallback((mantenimiento) => {
    setSelectedMantenimiento(mantenimiento);
    setShowEditModal(true);
  }, []);

  const handleDelete = useCallback(
    (mantenimiento) => {
      swal({
        title: "¿Estás seguro?",
        text: `El mantenimiento "${mantenimiento.titulo}" será eliminado permanentemente.`,
        icon: "warning",
        buttons: ["Cancelar", "Eliminar"],
        dangerMode: true,
      }).then(async (willDelete) => {
        if (willDelete) {
          try {
            await axiosInstance.delete(`${MANTENIMIENTOS_URL}${mantenimiento.id}/`);
            notifySuccess("Mantenimiento eliminado correctamente.");
            fetchMantenimientos(currentPage);
          } catch (error) {
            notifyError("No fue posible eliminar el mantenimiento.");
          }
        }
      });
    },
    [fetchMantenimientos, currentPage],
  );

  const handleIniciar = useCallback(
    async (mantenimiento) => {
      if (!mantenimiento?.uuid) {
        notifyError("No se encontró el identificador del mantenimiento.");
        return;
      }
      swal({
        title: "Iniciar mantenimiento",
        text: `¿Deseas iniciar el mantenimiento "${mantenimiento.titulo}"?`,
        icon: "info",
        buttons: ["Cancelar", "Iniciar"],
      }).then(async (willStart) => {
        if (willStart) {
          try {
            await iniciarMantenimiento(mantenimiento.uuid);
            notifySuccess("Mantenimiento iniciado correctamente.");
            fetchMantenimientos(currentPage);
          } catch (error) {
            notifyError("No fue posible iniciar el mantenimiento.");
          }
        }
      });
    },
    [iniciarMantenimiento, fetchMantenimientos, currentPage],
  );

  const handleCompletarClick = useCallback((mantenimiento) => {
    setSelectedMantenimiento(mantenimiento);
    setSelectedMantenimientoData({});
    setShowCompleteModal(true);
  }, []);

  const handleCancelarClick = useCallback((mantenimiento) => {
    setSelectedMantenimiento(mantenimiento);
    setSelectedMantenimientoData({});
    setShowCancelModal(true);
  }, []);

  const handleVerDetalles = useCallback((mantenimiento) => {
    setSelectedMantenimiento(mantenimiento);
    setShowDetailsModal(true);
  }, []);

  const handleConfirmCompletar = useCallback(async () => {
    if (!selectedMantenimiento?.uuid) return;
    try {
      await completarMantenimiento(selectedMantenimiento.uuid, {
        costo_mantenimiento: selectedMantenimientoData.costo || null,
      });
      notifySuccess("Mantenimiento completado correctamente.");
      setShowCompleteModal(false);
      fetchMantenimientos(currentPage);
    } catch (error) {
      notifyError("No fue posible completar el mantenimiento.");
    }
  }, [selectedMantenimiento, selectedMantenimientoData, completarMantenimiento, fetchMantenimientos, currentPage]);

  const handleConfirmCancelar = useCallback(async () => {
    if (!selectedMantenimiento?.uuid) return;
    try {
      await cancelarMantenimiento(selectedMantenimiento.uuid, selectedMantenimientoData.razon || "");
      notifySuccess("Mantenimiento cancelado correctamente.");
      setShowCancelModal(false);
      fetchMantenimientos(currentPage);
    } catch (error) {
      notifyError("No fue posible cancelar el mantenimiento.");
    }
  }, [selectedMantenimiento, selectedMantenimientoData, cancelarMantenimiento, fetchMantenimientos, currentPage]);

  const activoOptions = useMemo(
    () =>
      activos.map((act) => ({
        value: act.id,
        label: `${act.consecutivo} - ${act.nombre}`,
      })),
    [activos],
  );

  const columns = useMemo(
    () => [
      {
        Header: "Activo",
        accessor: (row) => {
          return (
            <span>
              {row.activo_nombre}
              <br />
              <small className="text-muted">
                {" "}
                {row.activo?.consecutivo}
              </small>
            </span>
          );
        },
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: "Título",
        accessor: "titulo",
        disableFilters: true,
      },
      {
        Header: "Estado",
        accessor: (row) => {
          const estados = {
            pendiente: { label: "Pendiente", className: "badge-warning" },
            programado: { label: "Programado", className: "badge-info" },
            en_proceso: { label: "En proceso", className: "badge-primary" },
            completado: { label: "Completado", className: "badge-success" },
            cancelado: { label: "Cancelado", className: "badge-danger" },
          };
          const estado = estados[row.estado] || { label: row.estado, className: "badge-secondary" };
          return <span className={`badge ${estado.className}`}>{estado.label}</span>;
        },
        id: "estado",
        disableFilters: true,
      },
      {
        Header: "Fecha mantenimiento",
        accessor: (row) => {
          if (!row.fecha_mantenimiento) return "-";
          const date = new Date(row.fecha_mantenimiento);
          return date.toLocaleDateString("es-CO");
        },
        id: "fecha_mantenimiento",
        disableFilters: true,
      },
      {
        Header: "Próximo mantenimiento",
        accessor: (row) => {
          if (!row.fecha_proximo_mantenimiento) return "-";
          const date = new Date(row.fecha_proximo_mantenimiento);
          return date.toLocaleDateString("es-CO");
        },
        id: "fecha_proximo_mantenimiento",
        disableFilters: true,
      },
      {
        Header: "Costo",
        accessor: "costo_mantenimiento",
        disableFilters: true,
      },
      {
        Header: "Realizado por",
        accessor: "realizado_por",
        disableFilters: true,
      },
      {
        Header: "Acciones",
        id: "acciones",
        disableFilters: true,
        disableSortBy: true,
        accessor: (row) => {
          if (!row) return null;
          const puedeIniciar = row.estado === "pendiente";
          const puedeCompletar = row.estado === "en_proceso";
          const puedeCancelar = !["completado", "cancelado"].includes(row.estado);
          const puedeEditar = !["completado", "cancelado"].includes(row.estado);

          return (
            <ButtonGroup aria-label="Acciones del mantenimiento">
              {puedeIniciar && (
                <CitoPlusLinkIconSuccess
                  title="Iniciar mantenimiento"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleIniciar(row);
                  }}
                >
                  <i className="fas fa-play" />
                </CitoPlusLinkIconSuccess>
              )}
              {puedeCompletar && (
                <CitoPlusLinkIconSuccess
                  title="Completar mantenimiento"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCompletarClick(row);
                  }}
                >
                  <i className="fas fa-check" />
                </CitoPlusLinkIconSuccess>
              )}
              {puedeCancelar && (
                <CitoPlusLinkIconWarning
                  title="Cancelar mantenimiento"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelarClick(row);
                  }}
                >
                  <i className="fas fa-times" />
                </CitoPlusLinkIconWarning>
              )}
              {puedeEditar && (
                <CitoPlusLinkIconPrimary
                  title="Editar mantenimiento"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(row);
                  }}
                >
                  <i className="fas fa-edit" />
                </CitoPlusLinkIconPrimary>
              )}
              <CitoPlusLinkIconPrimary
                title="Ver detalles"
                onClick={(e) => {
                  e.stopPropagation();
                  handleVerDetalles(row);
                }}
              >
                <i className="fas fa-eye" />
              </CitoPlusLinkIconPrimary>
              <CitoPlusLinkIconDanger
                title="Eliminar mantenimiento"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row);
                }}
              >
                <i className="fas fa-trash-alt" />
              </CitoPlusLinkIconDanger>
            </ButtonGroup>
          );
        },
      },
    ],
    [handleEdit, handleDelete, handleIniciar, handleCompletarClick, handleCancelarClick, handleVerDetalles],
  );

  const handleSuccess = useCallback(() => {
    fetchMantenimientos(currentPage);
  }, [fetchMantenimientos, currentPage]);

  return (
    <section className="card">
      <header className="card-header">
        <div className="d-flex flex-wrap align-items-center gap-2 w-100">
          <div className="d-flex flex-column me-auto flex-grow-1">
            <h2 className="card-title mb-0">Mantenimientos de activos</h2>
            <div className="text-muted small">
              Administra los mantenimientos de los activos del conjunto.
            </div>
          </div>
          <div className="d-flex gap-2 ms-auto">
            <button
              type="button"
              className="btn btn-primary btn-sm text-white"
              onClick={() => setShowCreateModal(true)}
            >
              <i className="fas fa-plus me-2" />
              Nuevo mantenimiento
            </button>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <i className="fas fa-circle-notch fa-spin" aria-hidden="true" />
                  <span>Exportando...</span>
                </span>
              ) : (
                <>
                  <i className="fas fa-file-excel me-2" />
                  Exportar
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm text-body"
              onClick={handleResetFilters}
              disabled={isLoading}
            >
              Limpiar filtros
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm text-white"
              onClick={handleConsult}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <i
                    className="fas fa-circle-notch fa-spin"
                    aria-hidden="true"
                  />
                  <span>Consultando...</span>
                </span>
              ) : (
                <>
                  <i className="fas fa-search me-2" />
                  Consultar
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="card-body">
        <div className="row g-3 mb-4">
          <div className="col-lg-3 col-md-6">
            <label htmlFor="activo-filter" className="form-label">
              Activo
            </label>
            <Select
              id="activo-filter"
              name="activo"
              value={activoFilter}
              onChange={handleActivoFilterChange}
              options={activoOptions}
              isLoading={isLoadingActivos}
              isClearable
              isSearchable
              placeholder="Buscar activo..."
              noOptionsMessage={() => "No hay activos"}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="fecha-desde" className="form-label">
              Fecha desde
            </label>
            <input
              id="fecha-desde"
              type="date"
              className="form-control"
              value={fechaDesde}
              onChange={handleFechaDesdeChange}
              disabled={isLoading}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="fecha-hasta" className="form-label">
              Fecha hasta
            </label>
            <input
              id="fecha-hasta"
              type="date"
              className="form-control"
              value={fechaHasta}
              onChange={handleFechaHastaChange}
              disabled={isLoading}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="search-term" className="form-label">
              Búsqueda
            </label>
            <input
              id="search-term"
              type="text"
              className="form-control"
              placeholder="Título o descripción"
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={isLoading}
            />
          </div>
        </div>

        {isLoading && (
          <div className="py-5 text-center">
            <i className="fas fa-circle-notch fa-spin fa-2x mb-2" />
            <div>Cargando mantenimientos...</div>
          </div>
        )}

        {!isLoading && mantenimientos.length === 0 && (
          <div className="alert alert-light border" role="alert">
            No se encontraron mantenimientos registrados.
          </div>
        )}

        {!isLoading && mantenimientos.length > 0 && (
          <Tables
            columns={columns}
            data={mantenimientos}
            loading={isLoading}
            totalPages={totalPages}
            currentPage={currentPage}
            fetchUrl={fetchMantenimientos}
            totalRecords={totalRecords}
            nextUrl={nextUrl}
            previousUrl={previousUrl}
            onSortingChange={handleSortingChange}
            sortField={sortField}
            order={order}
          />
        )}
      </div>

      <MantenimientoModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={handleSuccess}
        activos={activos}
        isLoadingActivos={isLoadingActivos}
        fetchActivos={fetchActivos}
      />

      <MantenimientoModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedMantenimiento(null);
        }}
        onSuccess={handleSuccess}
        mantenimiento={selectedMantenimiento}
        activos={activos}
        isLoadingActivos={isLoadingActivos}
        fetchActivos={fetchActivos}
      />

      <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Completar Mantenimiento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Deseas completar el mantenimiento <strong>{selectedMantenimiento?.titulo}</strong>?</p>
          <div className="form-group">
            <label>Costo del mantenimiento (opcional)</label>
            <input
              type="number"
              className="form-control"
              placeholder="Ingresa el costo"
              value={selectedMantenimientoData.costo || ""}
              onChange={(e) =>
                setSelectedMantenimientoData((prev) => ({
                  ...prev,
                  costo: e.target.value,
                }))
              }
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCompleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleConfirmCompletar}>
            Completar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancelar Mantenimiento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Estás seguro de cancelar el mantenimiento <strong>{selectedMantenimiento?.titulo}</strong>?</p>
          <div className="form-group">
            <label>Motivo de cancelación</label>
            <textarea
              className="form-control"
              placeholder="Ingresa el motivo de cancelación"
              value={selectedMantenimientoData.razon || ""}
              onChange={(e) =>
                setSelectedMantenimientoData((prev) => ({
                  ...prev,
                  razon: e.target.value,
                }))
              }
              rows={3}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmCancelar}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      <MantenimientoDetailsModal
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false);
          setSelectedMantenimiento(null);
        }}
        mantenimiento={selectedMantenimiento}
      />
    </section>
  );
};

const MantenimientoModal = ({
  show,
  onHide,
  onSuccess,
  mantenimiento,
  activos,
  isLoadingActivos,
  fetchActivos,
}) => {
  const [loading, setLoading] = useState(false);

  const isEditing = !!mantenimiento;

  const initialValues = isEditing
    ? {
        activo_id: mantenimiento.activo?.id || mantenimiento.activo_id || "",
        titulo: mantenimiento.titulo || "",
        descripcion: mantenimiento.descripcion || "",
        fecha_mantenimiento: mantenimiento.fecha_mantenimiento
          ? new Date(mantenimiento.fecha_mantenimiento).toISOString().split("T")[0]
          : "",
        fecha_proximo_mantenimiento: mantenimiento.fecha_proximo_mantenimiento
          ? new Date(mantenimiento.fecha_proximo_mantenimiento).toISOString().split("T")[0]
          : "",
        costo_mantenimiento: mantenimiento.costo_mantenimiento || "",
        realizado_por: mantenimiento.realizado_por || "",
      }
    : {
        activo_id: "",
        titulo: "",
        descripcion: "",
        fecha_mantenimiento: "",
        fecha_proximo_mantenimiento: "",
        costo_mantenimiento: "",
        realizado_por: "",
      };

  useEffect(() => {
    if (show && activos.length === 0 && !isLoadingActivos) {
      fetchActivos();
    }
  }, [show, activos.length, isLoadingActivos, fetchActivos]);

  const activoOptions = useMemo(
    () =>
      activos.map((act) => ({
        value: act.id,
        label: `${act.consecutivo} - ${act.nombre}`,
      })),
    [activos],
  );

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        activo_id: Number(values.activo_id),
        costo_mantenimiento:
          values.costo_mantenimiento === "" || values.costo_mantenimiento === null
            ? null
            : Number(values.costo_mantenimiento),
        fecha_mantenimiento: values.fecha_mantenimiento || null,
        fecha_proximo_mantenimiento: values.fecha_proximo_mantenimiento || null,
      };

      if (isEditing) {
        await axiosInstance.patch(`${MANTENIMIENTOS_URL}${mantenimiento.id}/`, payload);
        swal("Éxito", "Mantenimiento actualizado correctamente.", "success");
      } else {
        await axiosInstance.post(MANTENIMIENTOS_URL, payload);
        swal("Éxito", "Mantenimiento creado correctamente.", "success");
      }

      if (typeof onSuccess === "function") {
        onSuccess();
      }
      if (typeof onHide === "function") {
        onHide();
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        if (error.response.data) {
          setErrors(error.response.data);
        } else {
          swal(
            "Error",
            "La solicitud no es válida. Por favor, verifica los campos.",
            "error",
          );
        }
      } else {
        swal(
          "Error",
          "Ha ocurrido un error inesperado. Por favor, intenta nuevamente más tarde.",
          "error",
        );
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing
            ? `Editar mantenimiento: ${mantenimiento.titulo}`
            : "Crear mantenimiento"}
        </Modal.Title>
      </Modal.Header>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={mantenimientoSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Modal.Body>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="activo_id">
                      Activo <span className="text-danger">*</span>
                    </label>
                    <Select
                      id="activo_id"
                      name="activo_id"
                      value={activoOptions.find((opt) => opt.value === values.activo_id) || null}
                      onChange={(option) => setFieldValue("activo_id", option?.value || "")}
                      options={activoOptions}
                      isLoading={isLoadingActivos}
                      isClearable
                      isSearchable
                      placeholder="Buscar activo..."
                      noOptionsMessage={() => "No hay activos"}
                    />
                    {errors.activo_id && touched.activo_id && (
                      <div className="invalid-feedback d-block">{errors.activo_id}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="titulo">
                      Título <span className="text-danger">*</span>
                    </label>
                    <Field
                      name="titulo"
                      id="titulo"
                      className={`form-control${errors.titulo && touched.titulo ? " is-invalid" : ""}`}
                    />
                    {errors.titulo && touched.titulo && (
                      <div className="invalid-feedback">{errors.titulo}</div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor="descripcion">Descripción</label>
                    <Field
                      as="textarea"
                      name="descripcion"
                      id="descripcion"
                      rows={3}
                      className={`form-control${errors.descripcion && touched.descripcion ? " is-invalid" : ""}`}
                    />
                    {errors.descripcion && touched.descripcion && (
                      <div className="invalid-feedback">{errors.descripcion}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="fecha_mantenimiento">
                      Fecha mantenimiento <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="date"
                      name="fecha_mantenimiento"
                      id="fecha_mantenimiento"
                      className={`form-control${errors.fecha_mantenimiento && touched.fecha_mantenimiento ? " is-invalid" : ""}`}
                    />
                    {errors.fecha_mantenimiento && touched.fecha_mantenimiento && (
                      <div className="invalid-feedback">{errors.fecha_mantenimiento}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="fecha_proximo_mantenimiento">
                      Próximo mantenimiento
                    </label>
                    <Field
                      type="date"
                      name="fecha_proximo_mantenimiento"
                      id="fecha_proximo_mantenimiento"
                      className={`form-control${errors.fecha_proximo_mantenimiento && touched.fecha_proximo_mantenimiento ? " is-invalid" : ""}`}
                    />
                    {errors.fecha_proximo_mantenimiento && touched.fecha_proximo_mantenimiento && (
                      <div className="invalid-feedback">{errors.fecha_proximo_mantenimiento}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="costo_mantenimiento">Costo</label>
                    <Field
                      type="number"
                      name="costo_mantenimiento"
                      id="costo_mantenimiento"
                      step="0.01"
                      className={`form-control${errors.costo_mantenimiento && touched.costo_mantenimiento ? " is-invalid" : ""}`}
                    />
                    {errors.costo_mantenimiento && touched.costo_mantenimiento && (
                      <div className="invalid-feedback">{errors.costo_mantenimiento}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="realizado_por">Realizado por</label>
                    <Field
                      as="input"
                      type="text"
                      name="realizado_por"
                      id="realizado_por"
                      className={`form-control${errors.realizado_por && touched.realizado_por ? " is-invalid" : ""}`}
                      placeholder="Nombre de quien realizó el mantenimiento"
                    />
                    {errors.realizado_por && touched.realizado_por && (
                      <div className="invalid-feedback">{errors.realizado_por}</div>
                    )}
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onHide}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting || loading}
              >
                {loading ? (
                  <span className="d-inline-flex align-items-center gap-2">
                    <i className="fas fa-circle-notch fa-spin" />
                    Guardando...
                  </span>
                ) : isEditing ? (
                  "Actualizar"
                ) : (
                  "Crear"
                )}
              </Button>
            </Modal.Footer>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default ActivosMantenimientos;
